import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  User,
  Mail,
  Shield,
  Calendar,
  Building2,
  Edit,
  ArrowLeft,
  UserCheck,
  UserX,
  Trash2,
  Loader2,
  Activity,
  Clock,
  MapPin
} from 'lucide-react'
import { usersService } from '@/services/users'
import { userAreasService } from '@/services/areas'
import { ROUTES, USER_ROLES } from '@/utils/constants'
import { NavigationUtils } from '@/utils/navigation'
import toast from 'react-hot-toast'

const UserDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const {
    data: user,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['user', id],
    () => usersService.getById(id),
    {
      onError: (error) => {
        toast.error('Failed to load user details')
        console.error('Error loading user:', error)
      }
    }
  )

  // Fetch user's area assignments
  const { data: userAreasData } = useQuery(
    ['user-areas', id],
    () => userAreasService.getUserAreas(id, { page_size: 1000 }),
    {
      enabled: !!id && user && (user.role === 'area_manager' || user.role === 'user'),
      onError: (error) => {
        console.error('Error loading user areas:', error)
      }
    }
  )

  // Toggle user status mutation
  const toggleStatusMutation = useMutation(
    () => usersService.update(id, { is_active: !user.is_active }),
    {
      onSuccess: () => {
        toast.success('User status updated successfully')
        queryClient.invalidateQueries(['user', id])
        queryClient.invalidateQueries('users')
      },
      onError: (error) => {
        toast.error('Failed to update user status')
        console.error('Error updating user status:', error)
      }
    }
  )

  // Delete user mutation
  const deleteUserMutation = useMutation(
    () => usersService.delete(id),
    {
      onSuccess: () => {
        toast.success('User deleted successfully')
        NavigationUtils.navigateBackToSettings(navigate, 'users')
      },
      onError: (error) => {
        toast.error('Failed to delete user')
        console.error('Error deleting user:', error)
      }
    }
  )

  const handleToggleStatus = () => {
    toggleStatusMutation.mutate()
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    deleteUserMutation.mutate()
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800'
      case 'manager':
        return 'bg-blue-100 text-blue-800'
      case 'area_manager':
        return 'bg-purple-100 text-purple-800'
      case 'user':
        return 'bg-green-100 text-green-800'
      case 'viewer':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin'
      case 'manager':
        return 'Manager'
      case 'area_manager':
        return 'Area Manager'
      case 'user':
        return 'User'
      case 'viewer':
        return 'Viewer'
      default:
        return role || 'Unknown'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading user details...</span>
        </div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <User className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">User not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The user you're looking for doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'users')}
              className="btn-primary btn-sm"
            >
              Back to Users
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'users')}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user.first_name} {user.last_name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              User details and permissions
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleToggleStatus}
            className={`btn-sm flex items-center ${
              user.is_active
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
            disabled={toggleStatusMutation.isLoading}
          >
            {user.is_active ? <UserX className="h-4 w-4 mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
            {user.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <Link
            to={`/users/${user.id}/edit`}
            className="btn-primary btn-sm flex items-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit User
          </Link>
          <button
            onClick={handleDelete}
            className="btn-sm bg-red-100 text-red-700 hover:bg-red-200 flex items-center"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Information */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">User Information</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-lg font-medium text-purple-600">
                      {(user.first_name || user.username).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Full Name</div>
                    <div className="text-sm text-gray-600">
                      {user.first_name} {user.last_name}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Username</div>
                    <div className="text-sm text-gray-600">{user.username}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Email</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Role</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {getRoleDisplayName(user.role)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Activity className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Status</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Member Since</div>
                    <div className="text-sm text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Last Login</div>
                    <div className="text-sm text-gray-600">
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleDateString()
                        : 'Never'
                      }
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Last Updated</div>
                    <div className="text-sm text-gray-600">
                      {new Date(user.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Brand Access */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Brand Access</h3>
            </div>
            <div className="p-6">
              {user.role === USER_ROLES.ADMIN ? (
                <div className="text-center py-4">
                  <Shield className="h-8 w-8 mx-auto text-red-500 mb-2" />
                  <p className="text-sm text-gray-600">
                    Admin users have access to all brands
                  </p>
                </div>
              ) : user.brand_access && user.brand_access.length > 0 ? (
                <div className="space-y-3">
                  {user.brand_access.map((access) => (
                    <div key={access.brand_id} className="flex items-center space-x-3">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {access.brand?.name || `Brand ${access.brand_id}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {access.can_access ? 'Full Access' : 'No Access'}
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        access.can_access
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {access.can_access ? 'Granted' : 'Denied'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Building2 className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-600">
                    No brand access configured
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Area Access */}
        {(user.role === USER_ROLES.AREA_MANAGER || user.role === USER_ROLES.USER) && (
          <div className="lg:col-span-1">
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Area Access</h3>
              </div>
              <div className="p-6">
                {userAreasData?.user_areas && userAreasData.user_areas.length > 0 ? (
                  <div className="space-y-2">
                    {userAreasData.user_areas.map((userArea) => (
                      <div
                        key={userArea.id}
                        className="flex items-center p-3 bg-teal-50 rounded-md"
                      >
                        <MapPin className="h-4 w-4 text-teal-500 mr-3" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {userArea.area_name}
                          </p>
                          {userArea.brand_name && (
                            <p className="text-xs text-gray-500">
                              {userArea.brand_name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <MapPin className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-600">
                      No area access configured
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete <strong>{user.first_name} {user.last_name}</strong>?
                        This action cannot be undone and will remove all user data and access permissions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={confirmDelete}
                  disabled={deleteUserMutation.isLoading}
                >
                  {deleteUserMutation.isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete User'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteUserMutation.isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDetail
